import { Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import { Drawer } from "../../shared/Drawer";
import { DynamicGrid } from "../../dynamicGrid";
import { EmptyCell } from "../../dynamicGrid/columnDefinitions";
import { DynamicForm } from "../../dynamicForm";
import { dpiaByIdQueryOptions } from "../../api/dpia";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { CircleXIcon, YesIcon } from "../../shared/icons";
import type { DpiaById } from "../../api/dpia.types";
import { DpiaResponsibleSelect } from "./DpiaResponsible";
import { DpiaCollaboratorsSelect } from "./DpiaCollaborators";
import { DpiaOverviewDrawerHeader } from "./DpiaOverviewDrawerHeader";
import { DpiaSummaryField } from "./DpiaSummaryField";
import { DpiaUrlField } from "./DpiaUrlField";
import { CONNECTED_TENANT_INPUT_ID, DpiaConnectedTenantSelect } from "./DpiaConnectedTenant";

export const DpiaOverviewDrawer = () => {
  const dpiaId = DynamicGrid.useSelectedRowId();
  const { navigate, paths } = usePrivacyNavigate();
  const dpiaQuery = useQuery(dpiaByIdQueryOptions(dpiaId));

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!dpiaQuery.data) return <Drawer.Loading />;
        const dpia = dpiaQuery.data;
        const isReadOnly = !dpia.canEdit;

        return (
          <>
            <DpiaOverviewDrawerHeader
              dpia={dpia}
              activeTab="overview"
              onDrawerClose={() => navigate(paths.dpiaGrid())}
            />
            <Drawer.Content sx={{ pt: 3 }}>
              <Stack sx={{ mb: 5 }}>
                <DpiaConnectedTenantInline key={dpia.id} dpia={dpia} isReadOnly={isReadOnly} />
                <DpiaResponsibleInline dpia={dpia} isReadOnly={isReadOnly} />
                <DpiaCollaboratorsInline dpia={dpia} isReadOnly={isReadOnly} />
                {dpia.type === "external" ? (
                  <DpiaUrlField dpia={dpia} isReadOnly={isReadOnly} />
                ) : (
                  <DpiaPreAssessmentResult preAssessment={dpia.preAssessment} />
                )}
                <DpiaSummaryField dpia={dpia} isReadOnly={isReadOnly} />
              </Stack>
            </Drawer.Content>
          </>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

const DpiaConnectedTenantInline = ({ dpia, isReadOnly }: { dpia: DpiaById; isReadOnly: boolean }) => {
  const { t } = useTranslation();

  return (
    <DynamicForm.InlineFieldPrimary
      htmlFor={CONNECTED_TENANT_INPUT_ID}
      label={t("DpiaDrawer.ConnectedTenant.Label")}
      input={<DpiaConnectedTenantSelect dpia={dpia} isReadOnly={isReadOnly} />}
    />
  );
};

const DpiaResponsibleInline = ({ dpia, isReadOnly }: { dpia: DpiaById; isReadOnly: boolean }) => {
  const { t } = useTranslation();

  return (
    <DynamicForm.InlineFieldPrimary
      htmlFor="dpia-select-responsible"
      label={t("DpiaDrawer.Responsible.Label")}
      input={<DpiaResponsibleSelect dpia={dpia} isReadOnly={isReadOnly} />}
    />
  );
};

const DpiaCollaboratorsInline = ({ dpia, isReadOnly }: { dpia: DpiaById; isReadOnly: boolean }) => {
  const { t } = useTranslation();

  return (
    <DynamicForm.InlineFieldPrimary
      htmlFor="dpia-collaborators"
      label={t("DpiaDrawer.Collaborators.Label")}
      input={<DpiaCollaboratorsSelect dpia={dpia} isReadOnly={isReadOnly} />}
    />
  );
};

const DpiaPreAssessmentResult = ({ preAssessment }: { preAssessment: DpiaById["preAssessment"] }) => {
  const { t } = useTranslation();

  return (
    <DynamicForm.InlineFieldPrimary
      label={t("DpiaPreAssessment.PreAssessment")}
      input={
        <Stack direction="row" sx={{ pl: 2, alignItems: "center", gap: 1 }}>
          {!preAssessment.isCompleted || preAssessment.completionType === "skipped" ? (
            <EmptyCell />
          ) : (
            <>
              {preAssessment.dpiaRecommended ? (
                <YesIcon size={16} color={theme => theme.palette.primary.main} />
              ) : (
                <CircleXIcon size={16} />
              )}
              <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14) }}>
                {preAssessment.dpiaRecommended ? t("Dpia.Grid.DpiaRecommended") : t("Dpia.Grid.DpiaNotRequired")}
              </Typography>
            </>
          )}
        </Stack>
      }
    />
  );
};
